import React, { useEffect, useState, useCallback } from "react";
import { warehousesAPI } from "../../services/api";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, Lock, Unlock, FileJson } from "lucide-react";
import JsonImportBox from "../../components/JsonImportBox";
import { monthLabel } from "../../utils/month";

interface StocktakeItem { _id: string; name: string; unit: string; quantity: number; notes: string; }
interface StocktakeRecord {
  items: StocktakeItem[]; finalized: boolean; countedAt?: string;
  countedBy?: { _id: string; name: string } | null;
}

const INPUT = "w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-400";

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide bg-zinc-50 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 text-sm text-zinc-700 ${className}`}>{children}</td>;
}
function formatDate(iso: string) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function StocktakePanel({ warehouseId, month }: { warehouseId: string; month: string }) {
  const [record, setRecord]     = useState<StocktakeRecord | null>(null);
  const [loading, setLoading]   = useState(false);
  const [adding, setAdding]     = useState(false);
  const [jsonMode, setJsonMode] = useState(false);
  const [editItem, setEditItem] = useState<StocktakeItem | null>(null);
  const [form, setForm]         = useState({ name: "", unit: "", quantity: "0", notes: "" });
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await warehousesAPI.getStocktake(warehouseId, month); setRecord(res.data.data); }
    catch { toast.error("Lỗi tải kiểm kê"); }
    finally { setLoading(false); }
  }, [warehouseId, month]);

  useEffect(() => { load(); }, [load]);

  const finalized = !!record?.finalized;
  const readOnly = finalized;

  const openAdd = () => { setForm({ name: "", unit: "", quantity: "0", notes: "" }); setEditItem(null); setAdding(true); };
  const openEdit = (item: StocktakeItem) => {
    setForm({ name: item.name, unit: item.unit, quantity: String(item.quantity), notes: item.notes });
    setEditItem(item); setAdding(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nhập tên hàng"); return; }
    setSaving(true);
    try {
      const data = { name: form.name, unit: form.unit, quantity: Number(form.quantity) || 0, notes: form.notes };
      const res = editItem
        ? await warehousesAPI.updateStocktakeItem(warehouseId, month, editItem._id, data)
        : await warehousesAPI.addStocktakeItem(warehouseId, month, data);
      setRecord(res.data.data);
      setAdding(false);
      toast.success(editItem ? "Đã cập nhật" : "Đã thêm");
    } catch { toast.error("Lỗi lưu"); } finally { setSaving(false); }
  };

  const del = async (item: StocktakeItem) => {
    if (!window.confirm(`Xoá "${item.name}"?`)) return;
    try {
      const res = await warehousesAPI.deleteStocktakeItem(warehouseId, month, item._id);
      setRecord(res.data.data);
      toast.success("Đã xoá");
    } catch { toast.error("Lỗi xoá"); }
  };

  const finalize = async () => {
    if (!window.confirm(`Chốt kiểm kê tháng ${monthLabel(month)}? Bạn vẫn có thể mở khoá để sửa lại sau nếu cần.`)) return;
    try {
      const res = await warehousesAPI.finalizeStocktake(warehouseId, month, true);
      setRecord(res.data.data);
      toast.success("Đã chốt kiểm kê tháng");
    } catch { toast.error("Lỗi chốt kiểm kê"); }
  };

  const unlock = async () => {
    try {
      const res = await warehousesAPI.finalizeStocktake(warehouseId, month, false);
      setRecord(res.data.data);
      toast.success("Đã mở khoá, có thể chỉnh sửa");
    } catch { toast.error("Lỗi mở khoá"); }
  };

  const items = record?.items || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-zinc-800">{monthLabel(month)}</span>
        {finalized ? (
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
              <Lock size={11} /> Đã kiểm kê {record?.countedAt ? `ngày ${formatDate(record.countedAt)}` : ""}
              {record?.countedBy?.name ? ` · ${record.countedBy.name}` : ""}
            </span>
            <button onClick={unlock} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-indigo-600 font-medium">
              <Unlock size={12} /> Mở khoá
            </button>
          </div>
        ) : (
          <button onClick={finalize} className="flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg font-medium hover:bg-emerald-100">
            <Lock size={13} /> Chốt kiểm kê tháng
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-zinc-400 text-center py-3">Đang tải...</p>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-xl border border-amber-100">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-amber-100">
                  <Th className="w-8">#</Th>
                  <Th>Tên hàng</Th>
                  <Th className="w-24 text-center">ĐVT</Th>
                  <Th className="w-28 text-center">Tồn thực tế</Th>
                  <Th>Ghi chú</Th>
                  {!readOnly && <Th className="w-20 text-center">Thao tác</Th>}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !adding ? (
                  <tr><td colSpan={readOnly ? 5 : 6} className="px-4 py-8 text-center text-sm text-zinc-400">Chưa có số liệu kiểm kê cho {monthLabel(month)}</td></tr>
                ) : items.map((item, i) => (
                  <tr key={item._id} className="border-b border-amber-50 hover:bg-amber-50 transition-colors">
                    <Td className="text-zinc-400 text-xs">{i + 1}</Td>
                    <Td className="font-semibold text-zinc-800">{item.name}</Td>
                    <Td className="text-center text-zinc-500">{item.unit || "—"}</Td>
                    <Td className="text-center font-bold text-amber-700">{item.quantity}</Td>
                    <Td className="text-zinc-400 text-xs italic">{item.notes || "—"}</Td>
                    {!readOnly && (
                      <Td className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openEdit(item)} className="p-1.5 text-zinc-300 hover:text-indigo-600 touch-manipulation"><Pencil size={13}/></button>
                          <button onClick={() => del(item)}      className="p-1.5 text-zinc-300 hover:text-red-500 touch-manipulation"><Trash2 size={13}/></button>
                        </div>
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!readOnly && (
            jsonMode ? (
              <JsonImportBox
                fields={[
                  { key: "name",     label: "Tên hàng hoá",  required: true },
                  { key: "unit",     label: "Đơn vị tính" },
                  { key: "quantity", label: "Số lượng tồn đếm được" },
                  { key: "notes",    label: "Ghi chú" },
                ]}
                example={`[\n  {"name": "Khăn tắm", "unit": "cái", "quantity": 8},\n  {"name": "Dầu gội", "unit": "chai", "quantity": 3}\n]`}
                onImport={items => warehousesAPI.bulkAddStocktakeItems(warehouseId, month, items).then(res => setRecord(res.data.data))}
                onClose={() => setJsonMode(false)}
              />
            ) : adding ? (
              <div className="bg-white border border-zinc-200 rounded-xl p-3 space-y-2 mt-1">
                <input className={INPUT} placeholder="Tên hàng *" value={form.name} autoFocus
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                <div className="grid grid-cols-2 gap-2">
                  <input className={INPUT} placeholder="ĐVT (cái, hộp...)" value={form.unit}
                    onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
                  <input type="number" inputMode="numeric" min={0} className={INPUT} placeholder="Tồn thực tế" value={form.quantity}
                    onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
                </div>
                <input className={INPUT} placeholder="Ghi chú (tuỳ chọn)" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                <div className="flex gap-2 pt-1">
                  <button onClick={save} disabled={saving}
                    className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold disabled:opacity-50 touch-manipulation">
                    {saving ? "Đang lưu..." : editItem ? "Cập nhật" : "Thêm"}
                  </button>
                  <button onClick={() => setAdding(false)}
                    className="px-4 py-2 border border-zinc-200 rounded-lg text-sm text-zinc-600 touch-manipulation">Huỷ</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button onClick={openAdd}
                  className="flex items-center gap-1.5 text-sm text-amber-600 font-medium py-1 touch-manipulation">
                  <Plus size={14} /> Thêm hàng kiểm kê
                </button>
                <button onClick={() => setJsonMode(true)}
                  className="flex items-center gap-1.5 text-sm text-indigo-500 font-medium py-1 touch-manipulation">
                  <FileJson size={14} /> Nhập JSON
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
